import { supabase } from '../lib/supabase';
import { Pessoa, PessoaContato, PessoaEndereco, TipoCliente, CategoriaCliente } from '../types/pessoa';
import { Conta } from '../types/financeiro';
import { pessoaCascadeService } from './pessoaCascadeService';
import { sanitizePessoa } from '../utils/textSanitizer';
import { pessoaBelongsToGroup } from '../utils/pessoaGroupUtils';

export function isPessoaAtiva(p: any): boolean {
  if (!p) return false;
  // Qualquer sinal explícito de inatividade desqualifica, mesmo que "ativo"
  // esteja true - antes, "ativo === true" retornava logo na 2a linha e nunca
  // chegava a checar deleted_at/data_desativacao, deixando pessoas já
  // excluídas (soft-delete) aparecerem como ativas em todos os formulários.
  if (p.ativo === false) return false;
  if (p.data_desativacao && p.data_desativacao !== '') return false;
  if (p.deleted_at && p.deleted_at !== '') return false;
  if (p.situacao_cadastral && (p.situacao_cadastral.toUpperCase() === 'INATIVO' || p.situacao_cadastral.toUpperCase() === 'DESATIVADO')) return false;
  return true;
}

export async function getAgregadosMotoristasList(): Promise<{ id: number; nome: string }[]> {
  try {
    const pessoaIdsSet = new Set<number>();

    // 1. Grupos de pessoa (Agregado, Motorista, Terceiro, Autônomo, Transportador)
    try {
      const { data: grupos } = await supabase
        .from('grupo_pessoa')
        .select('id, nome');
      
      const targetGrupoIds = (grupos || [])
        .filter(g => {
          const nomeLower = (g.nome || '').toLowerCase();
          return (
            nomeLower.includes('agregado') ||
            nomeLower.includes('motorista') ||
            nomeLower.includes('terceiro') ||
            nomeLower.includes('autonomo') ||
            nomeLower.includes('autônomo') ||
            nomeLower.includes('transportador')
          );
        })
        .map(g => g.id);

      if (targetGrupoIds.length > 0) {
        const { data: pgData } = await supabase
          .from('pessoa_grupo')
          .select('pessoa_id')
          .in('grupo_pessoa_id', targetGrupoIds);

        (pgData || []).forEach(pg => {
          if (pg.pessoa_id) pessoaIdsSet.add(Number(pg.pessoa_id));
        });
      }
    } catch (gErr) {
      console.warn('Erro ao buscar grupos de agregados:', gErr);
    }

    // 2. Veículos (agregado_id e motorista_id)
    try {
      const { data: veiculosData } = await supabase
        .from('veiculos')
        .select('agregado_id, motorista_id');

      (veiculosData || []).forEach(v => {
        if (v.agregado_id) pessoaIdsSet.add(Number(v.agregado_id));
        if (v.motorista_id) pessoaIdsSet.add(Number(v.motorista_id));
      });
    } catch (vErr) {
      console.warn('Erro ao buscar pessoas dos veículos:', vErr);
    }

    // 3. Ordens de Serviço (campo agregado)
    try {
      const { data: osData } = await supabase
        .from('ordem_de_servico')
        .select('agregado')
        .not('agregado', 'is', null)
        .limit(2000);

      (osData || []).forEach(os => {
        if (os.agregado) pessoaIdsSet.add(Number(os.agregado));
      });
    } catch (osErr) {
      console.warn('Erro ao buscar agregados das OSs:', osErr);
    }

    const targetIds = Array.from(pessoaIdsSet);

    // Se encontramos IDs através de grupos, veículos ou OSs:
    if (targetIds.length > 0) {
      let allPessoas: any[] = [];
      const chunkSize = 200;
      for (let i = 0; i < targetIds.length; i += chunkSize) {
        const chunk = targetIds.slice(i, i + chunkSize);
        const { data } = await supabase
          .from('pessoa')
          .select('id, nome, ativo, data_desativacao, deleted_at, situacao_cadastral, tipo_cliente_id, categoria_cliente_id')
          .in('id', chunk);
        if (data) allPessoas.push(...data);
      }

      const filtered = allPessoas
        .filter(p => isPessoaAtiva(p))
        .map(p => ({ id: p.id, nome: (p.nome || '').trim() }))
        .filter(p => p.nome.length > 0)
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' }));

      if (filtered.length > 0) {
        return filtered;
      }
    }

    // Fallback: se não houver registros associados, busca pessoas filtrando com utilitário de grupo 'motoristas'
    const { data: allRaw } = await supabase
      .from('pessoa')
      .select('id, nome, ativo, data_desativacao, deleted_at, situacao_cadastral, tipo_cliente_id, categoria_cliente_id, documento')
      .order('nome');

    return (allRaw || [])
      .filter(p => isPessoaAtiva(p) && pessoaBelongsToGroup(p as any, 'motoristas'))
      .map(p => ({ id: p.id, nome: (p.nome || '').trim() }))
      .filter(p => p.nome.length > 0)
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' }));
  } catch (err) {
    console.error('Erro ao buscar lista de agregados/motoristas:', err);
    return [];
  }
}

/**
 * Retorna ESTRITAMENTE as pessoas cadastradas como CLIENTES.
 * Filtra por grupo de pessoa (Cliente), categorias de cliente (Construtora, Cliente em Geral, etc.)
 * e garante que motoristas/agregados/fornecedores não apareçam na seleção de clientes de propostas.
 */
export async function getClientesApenasList(currentClienteId?: number | string): Promise<{ id: number; nome: string; documento?: string; nome_fantasia?: string }[]> {
  try {
    const clientMap = new Map<number, { id: number; nome: string; documento?: string; nome_fantasia?: string }>();

    // 1. Pessoas com vínculo em grupo_pessoa do tipo 'Cliente'
    try {
      const { data: grupoCliente } = await supabase
        .from('grupo_pessoa')
        .select('id')
        .ilike('nome', '%cliente%');

      if (grupoCliente && grupoCliente.length > 0) {
        const gIds = grupoCliente.map(g => g.id);
        const { data: pgData } = await supabase
          .from('pessoa_grupo')
          .select('pessoa:pessoa_id(id, nome, nome_fantasia, documento, ativo, data_desativacao, deleted_at, situacao_cadastral)')
          .in('grupo_pessoa_id', gIds);

        (pgData || []).forEach((item: any) => {
          const p = Array.isArray(item.pessoa) ? item.pessoa[0] : item.pessoa;
          if (p && p.id && p.nome && (isPessoaAtiva(p) || String(p.id) === String(currentClienteId))) {
            clientMap.set(p.id, {
              id: p.id,
              nome: (p.nome || '').trim(),
              nome_fantasia: (p.nome_fantasia || '').trim(),
              documento: (p.documento || '').trim()
            });
          }
        });
      }
    } catch (gErr) {
      console.warn('Erro ao buscar clientes por grupo:', gErr);
    }

    // 2. Pessoas com categoria_cliente_id de clientes: 2 (Construtora), 5 (Cliente em Geral), 6 (Órgão do Governo)
    try {
      const { data: pCatData } = await supabase
        .from('pessoa')
        .select('id, nome, nome_fantasia, documento, ativo, data_desativacao, deleted_at, situacao_cadastral')
        .in('categoria_cliente_id', [2, 5, 6]);

      (pCatData || []).forEach((p: any) => {
        if (p && p.id && p.nome && (isPessoaAtiva(p) || String(p.id) === String(currentClienteId))) {
          clientMap.set(p.id, {
            id: p.id,
            nome: (p.nome || '').trim(),
            nome_fantasia: (p.nome_fantasia || '').trim(),
            documento: (p.documento || '').trim()
          });
        }
      });
    } catch (catErr) {
      console.warn('Erro ao buscar clientes por categoria:', catErr);
    }

    // 3. Se for edição de proposta e o cliente atual não estiver na lista, garante que ele seja incluído
    if (currentClienteId && !clientMap.has(Number(currentClienteId))) {
      try {
        const { data: pAtual } = await supabase
          .from('pessoa')
          .select('id, nome, nome_fantasia, documento')
          .eq('id', Number(currentClienteId))
          .maybeSingle();

        if (pAtual && pAtual.id && pAtual.nome) {
          clientMap.set(pAtual.id, {
            id: pAtual.id,
            nome: (pAtual.nome || '').trim(),
            nome_fantasia: (pAtual.nome_fantasia || '').trim(),
            documento: (pAtual.documento || '').trim()
          });
        }
      } catch (curErr) {
        console.warn('Erro ao buscar cliente atual:', curErr);
      }
    }

    // Se encontrou clientes pelo filtro especializado, retorna ordenado por nome
    if (clientMap.size > 0) {
      return Array.from(clientMap.values())
        .filter(c => c.nome.length > 0)
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' }));
    }

    // Fallback: exclui explicitamente motoristas, agregados e fornecedores
    const { data: allFallback } = await supabase
      .from('pessoa')
      .select('id, nome, nome_fantasia, documento, ativo, data_desativacao, deleted_at, situacao_cadastral')
      .order('nome');

    return (allFallback || [])
      .filter(p => isPessoaAtiva(p) || String(p.id) === String(currentClienteId))
      .map(p => ({
        id: p.id,
        nome: (p.nome || '').trim(),
        nome_fantasia: (p.nome_fantasia || '').trim(),
        documento: (p.documento || '').trim()
      }))
      .filter(p => p.nome.length > 0)
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' }));
  } catch (err) {
    console.error('Erro ao buscar lista estrita de clientes:', err);
    return [];
  }
}

export const pessoaService = {
  async getAll(): Promise<Pessoa[]> {
    let allPessoas: Pessoa[] = [];
    let from = 0;
    const step = 500;
    
    while (true) {
      let { data, error } = await supabase
        .from('pessoa')
        .select('*')
        .order('id', { ascending: false })
        .range(from, from + step - 1);

      if (error) {
        // Fallback retry without order
        const retry = await supabase
          .from('pessoa')
          .select('*')
          .range(from, from + step - 1);
        data = retry.data;
        error = retry.error;
      }

      if (error) {
        console.warn(`[getAll pessoas] Warning fetching page at offset ${from}:`, error.message);
        break;
      }
      
      if (data && data.length > 0) {
        allPessoas = [...allPessoas, ...data];
        if (data.length < step) {
          break;
        }
        from += step;
      } else {
        break;
      }
    }
    
    return allPessoas.map(sanitizePessoa);
  },

  async toggleAtivo(id: number, novoStatusAtivo: boolean): Promise<any> {
    if (!novoStatusAtivo) {
      // Inativação em cascata (veículos, motoristas, etc.)
      await pessoaCascadeService.desativarVinculosPessoa(id);
    }

    const payload: any = {
      ativo: novoStatusAtivo,
      data_desativacao: novoStatusAtivo ? null : new Date().toISOString(),
      situacao_cadastral: novoStatusAtivo ? 'ATIVO' : 'INATIVO',
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('pessoa')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.warn('[pessoaService.toggleAtivo] Retrying with graceful fallback:', error.message);
      // Fallback 1: try without 'ativo' column if missing
      const { data: data1, error: error1 } = await supabase
        .from('pessoa')
        .update({
          data_desativacao: novoStatusAtivo ? null : new Date().toISOString(),
          situacao_cadastral: novoStatusAtivo ? 'ATIVO' : 'INATIVO',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (!error1) return data1;

      // Fallback 2: try with deleted_at
      const { data: data2, error: error2 } = await supabase
        .from('pessoa')
        .update({
          deleted_at: novoStatusAtivo ? null : new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (!error2) return data2;

      // Fallback 3: try simple situacao_cadastral
      const { data: data3, error: error3 } = await supabase
        .from('pessoa')
        .update({
          situacao_cadastral: novoStatusAtivo ? 'ATIVO' : 'INATIVO',
        })
        .eq('id', id)
        .select()
        .single();

      if (error3) throw error;
      return data3;
    }

    return data;
  },

  async getById(id: number): Promise<Pessoa | null> {
    const { data, error } = await supabase
      .from('pessoa')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(pessoa: Omit<Pessoa, 'id'>): Promise<Pessoa> {
    const { data, error } = await supabase
      .from('pessoa')
      .insert([pessoa])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: number, pessoa: Partial<Pessoa>): Promise<Pessoa> {
    if (pessoa.ativo === false || (pessoa.situacao_cadastral && (pessoa.situacao_cadastral.toUpperCase() === 'INATIVO' || pessoa.situacao_cadastral.toUpperCase() === 'DESATIVADO'))) {
      await pessoaCascadeService.desativarVinculosPessoa(id);
    }

    const { data, error } = await supabase
      .from('pessoa')
      .update(pessoa)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: number): Promise<void> {
    await pessoaCascadeService.desativarVinculosPessoa(id);

    const { error } = await supabase
      .from('pessoa')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getTiposCliente(): Promise<TipoCliente[]> {
    const { data, error } = await supabase
      .from('tipo_cliente')
      .select('*');

    if (error) return [];
    return data || [];
  },

  async getCategoriasCliente(): Promise<CategoriaCliente[]> {
    const { data, error } = await supabase
      .from('categoria_cliente')
      .select('*');

    if (error) return [];
    return data || [];
  },

  async getContatosByPessoa(pessoaId: number): Promise<PessoaContato[]> {
    const { data, error } = await supabase
      .from('pessoa_contato')
      .select('*')
      .eq('pessoa_id', pessoaId);

    if (error) return [];
    return data || [];
  },

  async getEnderecosByPessoa(pessoaId: number): Promise<any[]> {
    const { data, error } = await supabase
      .from('pessoa_endereco')
      .select('*, cidade(nome)')
      .eq('pessoa_id', pessoaId);

    if (error) return [];
    return data || [];
  },

  async getGruposByPessoa(pessoaId: number): Promise<any[]> {
    const { data, error } = await supabase
      .from('pessoa_grupo')
      .select('*, grupo_pessoa(nome)')
      .eq('pessoa_id', pessoaId);

    if (error) return [];
    return data || [];
  },

  async getDadosBancariosByPessoa(pessoaId: number): Promise<any[]> {
    const { data, error } = await supabase
      .from('dados_bancarios')
      .select('*, banco:banco_id(nome), forma_pagamento:forma_pagamento_id(nome)')
      .eq('pessoa_id', pessoaId);

    if (error) return [];
    return data || [];
  },

  getAgregadosMotoristasList
};
